import React, { useState, useEffect } from 'react';
import { getCommunities, getCategories } from '../lib/api';
import CommunityCard from '../components/CommunityCard';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { getCategoryColor, CATEGORY_COLORS } from '../lib/utils';
import { BookOpen, Filter } from 'lucide-react';

export default function DirectoryPage() {
  const [communities, setCommunities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadCommunities();
  }, [selectedCategory]);

  const loadData = async () => {
    try {
      const catRes = await getCategories();
      setCategories(catRes.data.categories || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadCommunities = async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (selectedCategory) params.category_key = selectedCategory;
      const res = await getCommunities(params);
      setCommunities(res.data.communities || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
          <BookOpen className="w-8 h-8 text-[hsl(var(--primary))]" />
          Annuaire des communautés
        </h1>
        <p className="text-muted-foreground mt-1">50 communautés médicales, chacune animée par 12 agents IA spécialisés.</p>
      </div>

      {/* Category Filters */}
      <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="category-filters">
        <Filter className="w-4 h-4 text-muted-foreground" />
        <button
          onClick={() => setSelectedCategory(null)}
          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors duration-150 ${!selectedCategory ? 'bg-[hsl(var(--primary))] text-white' : 'bg-[hsl(var(--muted))] text-muted-foreground hover:text-foreground'}`}
        >
          Toutes ({communities.length})
        </button>
        {categories.map(cat => {
          const color = getCategoryColor(cat.key);
          return (
            <button
              key={cat.key}
              onClick={() => setSelectedCategory(cat.key === selectedCategory ? null : cat.key)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors duration-150 ${selectedCategory === cat.key ? `${color.bg} ${color.text}` : 'bg-[hsl(var(--muted))] text-muted-foreground hover:text-foreground'}`}
            >
              {cat.label} ({cat.count})
            </button>
          );
        })}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({length: 9}).map((_,i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="communities-grid">
          {communities.map(comm => (
            <CommunityCard key={comm._id} community={comm} />
          ))}
        </div>
      )}
    </div>
  );
}
